import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Smile } from 'lucide-react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '@/lib/utils';
import { PLATFORM_LABEL, type SocialPlatform } from '@/client/types/marketing';
import { usePerformanceSentimentQuery } from '@/client/api/marketing/radar';
import type { ApiPerformanceSentimentPoint, PerformanceRangeDays } from '@/client/types/marketing/api';
import { MarketingPageShell } from './components/MarketingPageShell';
import { EmptyState, Panel } from './components/marketing-ui';
import { PUBLIC_RANGE_PRESETS } from './posting-insights-data';
import {
  loadPublicRangePreference,
  savePublicRangePreference,
  servedWindowLabel,
  utcDayKey,
  utcDayTick,
} from './performance-range-storage';

const SENTIMENT_COLORS = {
  positive: '#059669',
  neutral: '#6b7280',
  negative: '#e11d48',
} as const;

const FILTER_PLATFORMS: SocialPlatform[] = ['x', 'instagram', 'facebook', 'linkedin', 'tiktok', 'youtube'];

function hoursLabel(hours: number | null | undefined): string {
  if (hours == null) return 'never';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m ago`;
  if (hours < 24) return `${hours.toFixed(1)}h ago`;
  return `${(hours / 24).toFixed(1)}d ago`;
}

function keyedPoints(rows: ApiPerformanceSentimentPoint[]): ApiPerformanceSentimentPoint[] {
  const byDate = new Map<string, ApiPerformanceSentimentPoint>();
  for (const p of rows) {
    const date = utcDayKey(p.date);
    if (!date) continue;
    const cur = byDate.get(date);
    if (!cur) {
      byDate.set(date, { ...p, date });
      continue;
    }
    byDate.set(date, {
      date,
      positive: cur.positive + p.positive,
      neutral: cur.neutral + p.neutral,
      negative: cur.negative + p.negative,
      unknown: cur.unknown + p.unknown,
      classified: cur.classified + p.classified,
    });
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/** % of classified; classified=0 → null so the line gaps instead of drawing 0. */
function alignNegativeShare(
  left: ApiPerformanceSentimentPoint[],
  right: ApiPerformanceSentimentPoint[],
  leftKey: string,
  rightKey: string,
) {
  const L = keyedPoints(left);
  const R = keyedPoints(right);
  const dates = [...new Set([...L.map((p) => p.date), ...R.map((p) => p.date)])].sort();
  const lm = new Map(L.map((p) => [p.date, p]));
  const rm = new Map(R.map((p) => [p.date, p]));
  return dates.map((date) => {
    const l = lm.get(date);
    const r = rm.get(date);
    return {
      date,
      [leftKey]: l && l.classified > 0 ? (l.negative / l.classified) * 100 : null,
      [rightKey]: r && r.classified > 0 ? (r.negative / r.classified) * 100 : null,
    };
  });
}

const SentimentAnalysisPage = ({ embedded = false }: { embedded?: boolean }) => {
  const [range, setRange] = useState<PerformanceRangeDays>(() => loadPublicRangePreference());
  const [subject, setSubject] = useState<'all' | 'brand' | 'competitor'>('all');
  const [mediaClass, setMediaClass] = useState<'all' | 'owned' | 'earned'>('all');
  const [platform, setPlatform] = useState<SocialPlatform | 'all'>('all');
  const [channel, setChannel] = useState<'all' | 'social' | 'traditional'>('all');

  const sentimentQ = usePerformanceSentimentQuery({
    range_days: range,
    ...(subject !== 'all' ? { subject } : {}),
    ...(mediaClass !== 'all' ? { media_class: mediaClass } : {}),
    ...(platform !== 'all' ? { platform } : {}),
    ...(channel !== 'all' ? { channel } : {}),
  });

  useEffect(() => {
    savePublicRangePreference(range);
  }, [range]);

  const payload = sentimentQ.data;
  const points = payload?.points ?? [];
  const corpus = payload?.corpus;
  const coverageRows = corpus?.coverage_rows ?? (points.length > 0 ? 1 : 0);
  const classified = corpus?.classified ?? points.reduce((s, p) => s + p.classified, 0);

  const countSeries = useMemo(
    () =>
      keyedPoints(points).map((p) => ({
        date: p.date,
        positive: p.positive,
        neutral: p.neutral,
        negative: p.negative,
        unknown: p.unknown,
        classified: p.classified,
        positiveShare: p.classified > 0 ? (p.positive / p.classified) * 100 : null,
        neutralShare: p.classified > 0 ? (p.neutral / p.classified) * 100 : null,
        negativeShare: p.classified > 0 ? (p.negative / p.classified) * 100 : null,
      })),
    [points],
  );

  const latest = countSeries[countSeries.length - 1];
  const latestShare = latest && latest.classified > 0 ? (latest.positive / latest.classified) * 100 : null;

  const bySubject = payload?.by_subject;
  const byMedia = payload?.by_media_class;
  const subjectChart = bySubject
    ? alignNegativeShare(bySubject.brand.points, bySubject.competitor.points, 'brand', 'competitor')
    : [];
  const mediaChart = byMedia
    ? alignNegativeShare(byMedia.owned.points, byMedia.earned.points, 'owned', 'earned')
    : [];

  const selectCls =
    'h-9 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none ring-blue-500 focus:ring-2';

  const rangeControls = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
        {PUBLIC_RANGE_PRESETS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              range === r ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
            )}
          >
            {r}d
          </button>
        ))}
      </div>
      <span className="text-xs tabular-nums text-gray-400">
        {servedWindowLabel(payload?.range_days, range)}
      </span>
      <select
        className={selectCls}
        value={channel}
        onChange={(e) => setChannel(e.target.value as typeof channel)}
      >
        <option value="all">All channels</option>
        <option value="social">Social</option>
        <option value="traditional">Traditional</option>
      </select>
      <select
        className={selectCls}
        value={subject}
        onChange={(e) => setSubject(e.target.value as typeof subject)}
      >
        <option value="all">All subjects</option>
        <option value="brand">Brand</option>
        <option value="competitor">Competitor</option>
      </select>
      <select
        className={selectCls}
        value={mediaClass}
        onChange={(e) => setMediaClass(e.target.value as typeof mediaClass)}
      >
        <option value="all">Owned + earned</option>
        <option value="owned">Owned</option>
        <option value="earned">Earned</option>
      </select>
      <select
        className={selectCls}
        value={platform}
        onChange={(e) => setPlatform(e.target.value as SocialPlatform | 'all')}
      >
        <option value="all">All platforms</option>
        {FILTER_PLATFORMS.map((p) => (
          <option key={p} value={p}>
            {PLATFORM_LABEL[p]}
          </option>
        ))}
      </select>
    </div>
  );

  const classifierBehind = coverageRows > 0 && classified === 0;
  const noCoverage = coverageRows === 0;

  const body = (
    <>
      {embedded && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            Daily counts from public coverage (social and traditional). Mix % uses classified only —
            unknown is never in the denominator. Window is {servedWindowLabel(payload?.range_days, range)}.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {rangeControls}
            {sentimentQ.isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
          </div>
        </div>
      )}
      {!embedded && (
        <p className="mb-4 text-sm text-gray-600">
          Live coverage sentiment is also on{' '}
          <Link to="/admin/marketing/radar/overview" className="font-medium text-blue-700 hover:underline">
            Radar Overview
          </Link>
          . Unknown is never folded into neutral. Default includes social and traditional — unlike
          Tracked, which is social only. Pick Social here if you want the two corpora to line up.
          Window is{' '}
          {servedWindowLabel(payload?.range_days, range)}.
        </p>
      )}

      {sentimentQ.isLoading ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-8 text-sm text-gray-500 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sentiment…
        </div>
      ) : sentimentQ.isError ? (
        <EmptyState label="Sentiment endpoint did not respond." />
      ) : noCoverage ? (
        <EmptyState label="No coverage in this window." />
      ) : (
        <>
          {classifierBehind && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
              <p className="font-medium">Coverage is here; the classifier is behind.</p>
              <p className="mt-1 text-amber-900/90">
                {corpus?.pending_classification ?? 0} pending
                {corpus
                  ? corpus.hours_since_classified == null
                    ? ' · never classified'
                    : ` · last classified ${hoursLabel(corpus.hours_since_classified)}`
                  : ''}
                . Chart stays up so this is not mistaken for an empty window.
              </p>
            </div>
          )}

          {corpus && corpus.owned_connected_posts > 0 && (
            <p className="mb-3 text-xs text-gray-500">
              Owned has {corpus.owned_connected_posts} connected posts; {corpus.owned_in_coverage}{' '}
              {corpus.owned_in_coverage === 1 ? 'is' : 'are'} in the public coverage corpus.
            </p>
          )}

          <Panel title="Sentiment over time">
            <p className="mb-2 text-xs text-gray-400">Counts per day. Unknown is excluded from the mix.</p>
            <div className="h-72">
              {countSeries.length === 0 ? (
                <p className="flex h-full items-center justify-center text-sm text-gray-400">
                  No daily points yet — classifier still catching up.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={countSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={utcDayTick} />
                    <YAxis tick={{ fontSize: 11 }} width={36} allowDecimals={false} />
                    <Tooltip labelFormatter={(d: string) => utcDayKey(d) || d} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="positive"
                      stackId="classified"
                      stroke={SENTIMENT_COLORS.positive}
                      fill={SENTIMENT_COLORS.positive}
                      fillOpacity={0.4}
                      name="Positive"
                    />
                    <Area
                      type="monotone"
                      dataKey="neutral"
                      stackId="classified"
                      stroke={SENTIMENT_COLORS.neutral}
                      fill={SENTIMENT_COLORS.neutral}
                      fillOpacity={0.4}
                      name="Neutral"
                    />
                    <Area
                      type="monotone"
                      dataKey="negative"
                      stackId="classified"
                      stroke={SENTIMENT_COLORS.negative}
                      fill={SENTIMENT_COLORS.negative}
                      fillOpacity={0.4}
                      name="Negative"
                    />
                    <Line
                      type="monotone"
                      dataKey="unknown"
                      stroke="#d97706"
                      strokeDasharray="4 4"
                      dot={{ r: 2, fill: '#d97706' }}
                      name="Unknown"
                      connectNulls={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </Panel>

          <Panel title="Classified mix" className="mt-4">
            <p className="mb-2 text-xs text-gray-400">
              Share of classified that day. Gaps where classified is 0 — unknown is not in this mix.
            </p>
            <div className="h-56">
              {countSeries.every((p) => p.classified === 0) ? (
                <p className="flex h-full items-center justify-center text-sm text-gray-400">
                  Nothing classified yet in this window.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={countSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={utcDayTick} />
                    <YAxis tick={{ fontSize: 11 }} width={36} unit="%" domain={[0, 100]} />
                    <Tooltip
                      labelFormatter={(d: string) => utcDayKey(d) || d}
                      formatter={(value, name) =>
                        typeof value !== 'number' ? ['—', String(name)] : [`${value.toFixed(0)}%`, String(name)]
                      }
                    />
                    <Legend />
                    <Line type="monotone" dataKey="positiveShare" name="Positive" stroke={SENTIMENT_COLORS.positive} dot={false} connectNulls={false} />
                    <Line type="monotone" dataKey="neutralShare" name="Neutral" stroke={SENTIMENT_COLORS.neutral} dot={false} connectNulls={false} />
                    <Line type="monotone" dataKey="negativeShare" name="Negative" stroke={SENTIMENT_COLORS.negative} dot={false} connectNulls={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Panel>

          {bySubject && (
            <Panel title="Brand vs competitor" className="mt-4">
              <p className="mb-2 text-xs text-gray-400">
                Parallel series — negative share of classified. Empty slice is a gap, not a zero line.
              </p>
              <div className="h-56">
                {subjectChart.every((p) => p.brand == null && p.competitor == null) ? (
                  <p className="flex h-full items-center justify-center text-sm text-gray-400">
                    {bySubject.brand.points.length === 0 && bySubject.competitor.points.length === 0
                      ? 'Both slices were quiet in this window.'
                      : 'Nothing classified on either slice yet.'}
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={subjectChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={utcDayTick} />
                      <YAxis tick={{ fontSize: 11 }} width={36} unit="%" />
                      <Tooltip
                        labelFormatter={(d: string) => utcDayKey(d) || d}
                        formatter={(value, name) =>
                          typeof value !== 'number' ? ['—', String(name)] : [`${value.toFixed(0)}%`, String(name)]
                        }
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="brand"
                        name="Brand"
                        stroke="#0f172a"
                        dot={false}
                        connectNulls={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="competitor"
                        name="Competitor"
                        stroke="#e11d48"
                        dot={false}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Panel>
          )}

          {byMedia && (
            <Panel title="Owned vs earned" className="mt-4">
              <p className="mb-2 text-xs text-gray-400">
                Who authored the post, still on the public basis. Not connected Owned analytics.
              </p>
              <div className="h-56">
                {mediaChart.every((p) => p.owned == null && p.earned == null) ? (
                  <p className="flex h-full items-center justify-center text-sm text-gray-400">
                    {byMedia.owned.points.length === 0 && byMedia.earned.points.length === 0
                      ? 'Both slices were quiet in this window.'
                      : 'Nothing classified on either slice yet.'}
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={mediaChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={utcDayTick} />
                      <YAxis tick={{ fontSize: 11 }} width={36} unit="%" />
                      <Tooltip
                        labelFormatter={(d: string) => utcDayKey(d) || d}
                        formatter={(value, name) =>
                          typeof value !== 'number' ? ['—', String(name)] : [`${value.toFixed(0)}%`, String(name)]
                        }
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="owned"
                        name="Owned"
                        stroke="#2563eb"
                        dot={false}
                        connectNulls={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="earned"
                        name="Earned"
                        stroke="#059669"
                        dot={false}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Panel>
          )}
        </>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <MarketingPageShell
      title="Sentiment analysis"
      description="Daily sentiment counts from Radar coverage (social and traditional). Brand and competitor stay on separate series."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Performance', href: '/admin/marketing/radar/performance' },
        { label: 'Sentiment analysis' },
      ]}
      icon={Smile}
      actions={rangeControls}
      stats={[
        {
          key: 'classified',
          label: 'Classified',
          value: String(classified),
        },
        {
          key: 'unknown',
          label: 'Unknown',
          value: String(corpus?.unknown ?? points.reduce((s, p) => s + p.unknown, 0)),
        },
        {
          key: 'positive',
          label: 'Latest positive',
          value: latestShare != null ? `${latestShare.toFixed(0)}%` : '—',
        },
        {
          key: 'coverage',
          label: 'Coverage (all channels)',
          value: String(corpus?.coverage_rows ?? points.length),
        },
      ]}
    >
      {body}
    </MarketingPageShell>
  );
};

export default SentimentAnalysisPage;
